import { useState, useEffect } from 'react';
import axios from 'axios';
import PublicHeader from '../components/PublicHeader';
import { PaymentMethodPicker } from '../components/PaymentMethodPicker';
import {
  BookOpen,
  Search,
  Euro,
  FileText,
  Video,
  Link as LinkIcon,
  ShoppingCart,
  CheckCircle,
  Copy,
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function PublicResources() {
  const [resources, setResources] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState('all');
  const [selected, setSelected] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [showBankInfo, setShowBankInfo] = useState(false);
  const [bankInfo, setBankInfo] = useState(null);
  const [emailStatus, setEmailStatus] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState('card');
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    company: '',
  });

  useEffect(() => {
    fetchResources();
  }, []);

  useEffect(() => {
    let list = resources;
    if (search) {
      const s = search.toLowerCase();
      list = list.filter(
        (r) =>
          r.title.toLowerCase().includes(s) ||
          r.description.toLowerCase().includes(s)
      );
    }
    if (category !== 'all') {
      list = list.filter((r) => r.category === category);
    }
    setFiltered(list);
  }, [resources, search, category]);

  const fetchResources = async () => {
    try {
      const res = await axios.get(`${API}/public/resources`);
      setResources(res.data);
    } catch (e) {
      console.error('Error fetching resources:', e);
    } finally {
      setLoading(false);
    }
  };

  const categories = [...new Set(resources.map((r) => r.category))];

  const openPurchase = (resource) => {
    setSelected(resource);
    setShowForm(true);
  };

  const handlePurchase = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/public/resource-orders`, {
        ...formData,
        resource_id: selected.id,
        payment_method: paymentMethod,
        origin_url: window.location.origin,
      });
      if (res.data.checkout_url) {
        window.location.href = res.data.checkout_url;
        return;
      }
      setBankInfo(res.data.bank_info);
      setEmailStatus(res.data.email_status || 'skipped');
      setShowForm(false);
      setShowBankInfo(true);
      setFormData({ first_name: '', last_name: '', email: '', phone: '', company: '' });
    } catch (err) {
      alert(err.response?.data?.detail || 'Erreur lors de la commande');
    } finally {
      setSubmitting(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
  };

  const iconFor = (type) => {
    if (type === 'video') return <Video className="text-fuchsia-500" size={28} />;
    if (type === 'link') return <LinkIcon className="text-purple-500" size={28} />;
    return <FileText className="text-violet-500" size={28} />;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-violet-50 via-purple-50 to-fuchsia-50">
      <PublicHeader />

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="text-center mb-10">
          <h2 className="text-4xl md:text-5xl font-bold text-gray-800 mb-3">
            Nos <span className="gradient-text">Ressources Pédagogiques</span>
          </h2>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Guides, supports et documents conçus par Insitumed pour vous accompagner dans votre pratique.
          </p>
        </div>

        {/* Filters */}
        <div className="mb-8 flex flex-col md:flex-row gap-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
            <input
              type="text"
              placeholder="Rechercher une ressource..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="input-violet !pl-10"
              data-testid="resources-search-input"
            />
          </div>
          <select
            value={category}
            onChange={(e) => setCategory(e.target.value)}
            className="input-violet md:!w-64 shrink-0"
            data-testid="resources-category-select"
          >
            <option value="all">Toutes les catégories</option>
            {categories.map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </select>
        </div>

        {loading ? (
          <div className="text-center py-16 text-gray-500">Chargement...</div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-16">
            <BookOpen className="mx-auto text-gray-300 mb-4" size={64} />
            <p className="text-gray-500 text-lg">Aucune ressource disponible pour le moment.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filtered.map((r) => (
              <div
                key={r.id}
                className="bg-white rounded-2xl shadow-lg border border-violet-100 overflow-hidden hover:shadow-2xl transition-all"
                data-testid={`resource-public-card-${r.id}`}
              >
                <div className="bg-gradient-to-br from-violet-500 to-purple-600 h-32 flex items-center justify-center">
                  <div className="p-4 bg-white/20 backdrop-blur-sm rounded-2xl">
                    {iconFor(r.resource_type)}
                  </div>
                </div>

                <div className="p-6">
                  <span className="inline-block px-3 py-1 bg-violet-100 text-violet-700 rounded-full text-xs font-semibold mb-3">
                    {r.category}
                  </span>
                  <h3 className="text-lg font-bold text-gray-800 mb-2 line-clamp-2">{r.title}</h3>
                  <p className="text-sm text-gray-600 mb-4 line-clamp-3">{r.description}</p>

                  <div className="flex items-center justify-between pt-4 border-t border-gray-100">
                    <div className="flex items-center gap-1 text-violet-600 font-bold text-xl">
                      <Euro size={20} />
                      <span>{Number(r.price).toFixed(2).replace('.', ',')}</span>
                    </div>
                    <button
                      onClick={() => openPurchase(r)}
                      className="btn-violet flex items-center gap-2 text-sm"
                      data-testid={`resource-buy-${r.id}`}
                    >
                      <ShoppingCart size={16} />
                      Acheter
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </main>

      {/* Purchase form modal */}
      {showForm && selected && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl max-w-lg w-full p-8 max-h-[90vh] overflow-y-auto">
            <h3 className="text-2xl font-bold text-gray-800 mb-2">Commander cette ressource</h3>
            <p className="text-sm text-gray-600 mb-2">
              <strong>{selected.title}</strong> · {Number(selected.price).toFixed(2).replace('.', ',')} €
            </p>
            <p className="text-xs text-gray-500 mb-6">
              Par carte, la ressource est disponible immédiatement dans votre espace apprenant. Par virement, elle l'est dès réception du paiement.
            </p>

            <form onSubmit={handlePurchase} className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <input
                  type="text"
                  required
                  placeholder="Prénom"
                  value={formData.first_name}
                  onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                  className="input-violet"
                  data-testid="resource-order-firstname"
                />
                <input
                  type="text"
                  required
                  placeholder="Nom"
                  value={formData.last_name}
                  onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                  className="input-violet"
                  data-testid="resource-order-lastname"
                />
              </div>
              <input
                type="email"
                required
                placeholder="Email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                className="input-violet"
                data-testid="resource-order-email"
              />
              <input
                type="tel"
                placeholder="Téléphone (optionnel)"
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                className="input-violet"
              />
              <input
                type="text"
                placeholder="Entreprise (optionnel)"
                value={formData.company}
                onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                className="input-violet"
              />

              <PaymentMethodPicker value={paymentMethod} onChange={setPaymentMethod} />

              <div className="flex gap-3 pt-3">
                <button
                  type="submit"
                  disabled={submitting}
                  className="btn-violet flex-1 disabled:opacity-50"
                  data-testid="resource-order-submit"
                >
                  {submitting ? 'Envoi...' : paymentMethod === 'card' ? 'Payer par carte' : 'Valider la commande'}
                </button>
                <button
                  type="button"
                  onClick={() => setShowForm(false)}
                  className="px-6 py-3 border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  Annuler
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Bank info modal */}
      {showBankInfo && bankInfo && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid="bank-info-modal">
          <div className="bg-white rounded-2xl max-w-lg w-full p-8">
            <div className="text-center mb-6">
              <CheckCircle className="text-green-500 mx-auto mb-3" size={56} />
              <h3 className="text-2xl font-bold text-gray-800">Commande enregistrée !</h3>
              {emailStatus === 'success' ? (
                <p className="text-sm text-gray-600 mt-2" data-testid="email-status-success">
                  Un email vous a été envoyé avec les coordonnées ci-dessous. Effectuez le virement en indiquant la référence exacte.
                </p>
              ) : (
                <p className="text-sm text-gray-600 mt-2" data-testid="email-status-failure">
                  Votre commande est enregistrée. <strong>Notez ou copiez les coordonnées bancaires ci-dessous</strong> pour effectuer le virement en indiquant la référence exacte. (L'envoi automatique par email n'a pas pu aboutir — vous pouvez aussi les recopier maintenant.)
                </p>
              )}
            </div>

            <div className="bg-violet-50 rounded-xl p-5 space-y-3 border border-violet-100">
              {[
                ['Bénéficiaire', bankInfo.holder],
                ['IBAN', bankInfo.iban],
                ['BIC', bankInfo.bic],
                ['Montant', `${Number(bankInfo.amount).toFixed(2).replace('.', ',')} €`],
                ['Référence', bankInfo.reference],
              ].map(([label, val]) => (
                <div key={label} className="flex items-center justify-between text-sm">
                  <div>
                    <div className="text-gray-500 text-xs">{label}</div>
                    <div className="font-mono font-semibold text-gray-800">{val}</div>
                  </div>
                  <button
                    onClick={() => copyToClipboard(String(val))}
                    className="p-2 text-violet-600 hover:bg-violet-100 rounded-lg"
                    title="Copier"
                  >
                    <Copy size={16} />
                  </button>
                </div>
              ))}
            </div>

            <p className="text-xs text-gray-500 mt-4 text-center">
              Une fois le virement reçu, votre ressource sera activée sous 48 h dans votre espace apprenant (
              <a href="/mon-espace" className="text-violet-600 underline">/mon-espace</a>
              ).
            </p>

            <button
              onClick={() => setShowBankInfo(false)}
              className="btn-violet w-full mt-6"
              data-testid="close-bank-info"
            >
              J'ai compris
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
